import React, { useEffect, useState } from 'react'
import { useTranslation } from 'i18n'

import { formatStepImageUrl } from 'util/beaker2-step-image-url'

import {
  checklistHint,
  isAtCap,
  isInputSatisfied,
  maxSelections,
  selectedValues,
} from '../utils/checklist'

import RichText, { plainText } from './RichText'

import * as styles from '../ImageEnhancement.module.scss'

/*
  CustomizeStage: renders the configured inputs (cards / checklist / select / toggle / free
  text) plus the confirmation checkboxes, and gates the Generate button until required
  inputs + checkboxes are set.

  values:  map of input key -> chosen option value / text, or an array of option values
           for a CHECKLIST input
  checked: map of checkbox id -> boolean
*/
/*
  Cards fill the row rather than sitting at a fixed width, so the column count comes from
  how many options the CMS configured: up to 4 they all share one row, and beyond that we
  pick 3 or 4 columns - whichever leaves the last row least ragged (6 -> 3, 8 -> 4).
*/
const MAX_CARDS_PER_ROW = 4

const cardsPerRow = count => {
  if (count <= MAX_CARDS_PER_ROW) return Math.max(count, 1)
  const orphans = columns => (columns - (count % columns)) % columns
  return orphans(4) <= orphans(3) ? 4 : 3
}

// Phones cannot carry 3+ image cards across, so pair them up instead.
const cardsPerRowMobile = count => (count <= 2 ? Math.max(count, 1) : 2)

/*
  OptionCard doubles as a radio-style card (CARDS) and a tick-box card (CHECKLIST).

  The multi-select variant stays a <button> with aria-pressed rather than becoming a hidden
  <input type="checkbox"> behind a label: the card is already a button, every .optionCard
  style targets that element, and aria-pressed is the standard toggle-button pattern. A
  hidden checkbox would mean forking the component or changing the markup the single-select
  cards render today, for no gain in what assistive tech announces.

  At the cap the unticked cards use aria-disabled rather than the disabled attribute, so
  they stay focusable and a keyboard user can still find out they exist. The click still
  reaches onSelect; it is the caller that decides whether it changes the selection or
  explains why it cannot.
*/
const OptionCard = ({ option, selected, multi = false, disabled = false, onSelect }) => {
  const hasText =
    (option.title && option.title.trim()) ||
    (option.description && option.description.trim())
  return (
    <button
      type="button"
      className={`${styles.optionCard} ${selected ? styles.selected : ''} ${
        multi ? styles.optionCardMulti : ''
      }`}
      aria-pressed={multi ? selected : undefined}
      aria-disabled={disabled ? true : undefined}
      onClick={onSelect}
    >
      {multi && <span className={styles.optionTick} aria-hidden="true" />}
      {option.thumbnailId && (
        <img
          className={styles.optionImage}
          src={formatStepImageUrl(option.thumbnailId)}
          alt={option.title || ''}
        />
      )}
      {hasText && (
        <div className={styles.optionBody}>
          <RichText as="div" className={styles.optionTitle} html={option.title} />
          <RichText as="div" className={styles.optionDescription} html={option.description} />
        </div>
      )}
    </button>
  )
}

/*
  The checklist grid, which owns the cap notice.

  At the cap the unticked cards are only aria-disabled, so a keyboard user can land on one
  and press Enter - and get nothing back. The one explanation on the page is the static
  hint above the grid, which was already there before the block and says nothing about
  this press. A blocked click writes the way out into a polite live region instead, so it
  is announced when it happens without interrupting what is being read.
*/
const ChecklistGrid = ({ input, value, labelId, gridStyle, onChange }) => {
  const { t } = useTranslation('imageEnhancement')
  const [capNotice, setCapNotice] = useState('')
  const options = input.options || []
  const selected = selectedValues(value)
  const atCap = isAtCap(input, value)

  // The notice belongs to one blocked click, not to the cap: as soon as the user frees a
  // slot it goes, so reaching the cap again cannot re-announce it without a new click.
  useEffect(() => {
    if (!atCap) setCapNotice('')
  }, [atCap])

  return (
    <>
      <div
        className={styles.cardGrid}
        style={gridStyle}
        role="group"
        aria-labelledby={labelId}
      >
        {options.map((opt, i) => {
          const isSelected = selected.includes(opt.value)
          const blocked = atCap && !isSelected
          return (
            <OptionCard
              // Two options may declare the same value, so the index keeps the keys apart.
              key={`${opt.value}-${i}`}
              option={opt}
              multi
              selected={isSelected}
              disabled={blocked}
              onSelect={() =>
                blocked
                  ? setCapNotice(t('checklistCapReached', { count: maxSelections(input) }))
                  : onChange(input.key, opt.value, { multiple: true })
              }
            />
          )
        })}
      </div>
      <p className={styles.capNotice} role="status" aria-live="polite">
        {capNotice}
      </p>
    </>
  )
}

const InputControl = ({ input, value, labelId, onChange }) => {
  const { t } = useTranslation('imageEnhancement')
  const options = input.options || []
  const gridStyle = {
    '--cards-per-row': cardsPerRow(options.length),
    '--cards-per-row-mobile': cardsPerRowMobile(options.length),
  }

  switch (input.type) {
    case 'CARDS':
      return (
        <div className={styles.cardGrid} style={gridStyle}>
          {options.map(opt => (
            <OptionCard
              key={opt.value}
              option={opt}
              selected={value === opt.value}
              onSelect={() => onChange(input.key, opt.value)}
            />
          ))}
        </div>
      )
    case 'CHECKLIST':
      return (
        <ChecklistGrid
          input={input}
          value={value}
          labelId={labelId}
          gridStyle={gridStyle}
          onChange={onChange}
        />
      )
    case 'TOGGLE':
      return (
        <div className={styles.toggleRow}>
          {options.map(opt => (
            <button
              key={opt.value}
              type="button"
              className={`${styles.togglePill} ${value === opt.value ? styles.selected : ''}`}
              aria-pressed={value === opt.value}
              onClick={() => onChange(input.key, opt.value)}
            >
              {/* Titles are authored as rich text, like every other option title; printing
                  the string would show the markup. */}
              {opt.title ? <RichText html={opt.title} /> : opt.value}
            </button>
          ))}
        </div>
      )
    case 'TEXT':
      return (
        <input
          className={styles.textField}
          type="text"
          value={value || ''}
          placeholder={input.placeholder || ''}
          maxLength={input.maxLength || 40}
          onChange={e => onChange(input.key, e.target.value)}
        />
      )
    case 'SELECT':
    default:
      return (
        <select
          className={styles.select}
          value={value || ''}
          onChange={e => onChange(input.key, e.target.value)}
        >
          <option value="">{t('selectPlaceholder')}</option>
          {options.map(opt => (
            // An <option> renders text and nothing else, so the authored markup has to come
            // off rather than be handed to the browser as content.
            <option key={opt.value} value={opt.value}>
              {plainText(opt.title) || opt.value}
            </option>
          ))}
        </select>
      )
  }
}

const CustomizeStage = ({
  copy,
  cardClassName,
  inputs,
  checkboxes,
  values,
  checked,
  onChangeValue,
  onToggleCheckbox,
  onGenerate,
}) => {
  const { t } = useTranslation('imageEnhancement')
  // Every input is checked, not only the required ones: a CHECKLIST can carry a minimum
  // that applies once the user has ticked anything, required or not.
  const requiredInputsMet = inputs.every(i => isInputSatisfied(i, values[i.key]))

  const requiredCheckboxesMet = checkboxes
    .filter(c => c.required)
    .every(c => checked[c.id])

  const canGenerate = requiredInputsMet && requiredCheckboxesMet

  return (
    <div className={cardClassName}>
      <div className={styles.cardHeader}>
        <RichText as="h2" className={styles.title} html={copy.customizeTitle} />
        <RichText as="p" className={styles.subtitle} html={copy.customizeSubtitle} />
      </div>

      {inputs.map(input => {
        const labelId = `ie-input-label-${input.key}`
        const hint = input.type === 'CHECKLIST' ? checklistHint(input, t) : ''
        return (
          <div key={input.key} className={styles.inputBlock}>
            <label className={styles.inputLabel} id={labelId}>
              <RichText html={input.label} />
              {input.required ? ' *' : ''}
            </label>
            <RichText className={styles.helpText} html={input.helpText} />
            {hint && <p className={styles.helpText}>{hint}</p>}
            <InputControl
              input={input}
              value={values[input.key]}
              labelId={labelId}
              onChange={onChangeValue}
            />
          </div>
        )
      })}

      {checkboxes.map(cb => (
        <label key={cb.id} className={styles.checkboxRow}>
          <input
            type="checkbox"
            checked={!!checked[cb.id]}
            onChange={() => onToggleCheckbox(cb.id)}
          />
          <span dangerouslySetInnerHTML={{ __html: cb.label }} />
        </label>
      ))}

      <button
        type="button"
        className={styles.btn}
        disabled={!canGenerate}
        onClick={onGenerate}
      >
        {copy.generateCtaText || t('generateCta')}
      </button>
    </div>
  )
}

export default CustomizeStage
